// =============================================================================
// src/hooks/useSyncStatus.js
//
// Fixed version: ensures every consumer re-syncs from module state on mount,
// and broadcasts state changes via microtask to avoid React batching issues.
// Includes diagnostic console.logs — remove once stable.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { onEvent } from '../socket';

const subscribers = new Set();
let currentState = {
  status: 'idle',
  stage: null,
  lastCompletedAt: null,
  lastErrorAt: null,
  lastCompletePayload: null,
  lastErrorPayload: null,
};
let socketSubscribed = false;

function broadcast() {
  queueMicrotask(() => {
    const snapshot = currentState;
    for (const fn of subscribers) {
      try { fn(snapshot); } catch (e) { console.error('[useSyncStatus] subscriber error', e); }
    }
  });
}

function setState(next) {
  currentState = { ...currentState, ...next };
  console.log('[useSyncStatus] state →', currentState.status, currentState.stage);
  broadcast();
}

function ensureSocketSubscribed() {
  if (socketSubscribed) return;
  socketSubscribed = true;

  onEvent('sync:progress', (payload) => {
    console.log('[useSyncStatus] ← sync:progress', payload);
    setState({ status: 'syncing', stage: payload?.stage || null });
  });

  onEvent('sync:complete', (payload) => {
    console.log('[useSyncStatus] ← sync:complete', payload);
    setState({
      status: 'idle',
      stage: null,
      lastCompletedAt: Date.now(),
      lastCompletePayload: payload,
    });
  });

  onEvent('sync:error', (payload) => {
    console.log('[useSyncStatus] ← sync:error', payload);
    setState({
      status: 'idle',
      stage: null,
      lastErrorAt: Date.now(),
      lastErrorPayload: payload,
    });
  });
}

export function useSyncStatus() {
  const [state, setLocal] = useState(currentState);

  useEffect(() => {
    ensureSocketSubscribed();
    // Re-sync from current module state in case it changed between
    // initial useState() and this effect running
    setLocal(currentState);
    subscribers.add(setLocal);
    return () => { subscribers.delete(setLocal); };
  }, []);

  const markStarted = useCallback(() => {
    setState({ status: 'syncing', stage: 'starting' });
  }, []);

  const markIdle = useCallback(() => {
    setState({ status: 'idle', stage: null });
  }, []);

  return {
    status: state.status,
    stage: state.stage,
    isSyncing: state.status === 'syncing',
    lastCompletedAt: state.lastCompletedAt,
    lastErrorAt: state.lastErrorAt,
    markStarted,
    markIdle
  };
}

export function useOnSyncComplete(callback) {
  const cbRef = useRef(callback);
  cbRef.current = callback;

  const lastSeenRef = useRef(currentState.lastCompletedAt);

  useEffect(() => {
    console.log('[useOnSyncComplete] mounting, initial lastSeen=', lastSeenRef.current);
    ensureSocketSubscribed();
    const subscriber = (state) => {
      console.log('[useOnSyncComplete] received state, lastCompletedAt=', state.lastCompletedAt, 'lastSeen=', lastSeenRef.current);
      if (state.lastCompletedAt && state.lastCompletedAt !== lastSeenRef.current) {
        lastSeenRef.current = state.lastCompletedAt;
        console.log('[useOnSyncComplete] firing callback');
        cbRef.current?.(state.lastCompletePayload);
      }
    };
    subscribers.add(subscriber);
    return () => { subscribers.delete(subscriber); };
  }, []);
}